import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-internal-assessment-grace-marks-guidelines-2026",
  title: "DU Internal Assessment Grievance &amp; Grace Marks Rules 2026: IAMC Portal, Deadlines &amp; Audit Trail",
  description:
    "Complete 4,000-word guide to Delhi University Internal Assessment Moderation Committee (IAMC) 2026. Breakdown-wise IA component scoring (attendance, tests, assignments, presentations), grace marks rules, grievance portal filing, IAMC hearing process, and audit trail documentation.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "college-life",
  tags: ["DU", "internal-assessment", "IAMC", "grace-marks", "moderation-committee", "attendance", "DU-grading"],
  readMinutes: 22,
  heroEyebrow: "College · Assessment &amp; Grievance Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Internal Assessment (IA) constitutes <strong>25 marks out of the total 100 marks</strong> for every paper in Delhi University's UGCF 2022 framework (also known as NEP 2020 implementation at DU). Unlike the end-semester examination (75 marks) which is externally evaluated by anonymous examiners, the IA is awarded by your course teacher based on continuous evaluation throughout the semester. This seemingly simple 25-mark component generates more student grievances than any other aspect of DU's grading system. This guide provides the complete rules, grievance procedures, and strategic approaches.
      </p>

      {/* ─── Section 1: IA Component Breakdown ─── */}
      <h2>1. Internal Assessment Component Breakdown (25 Marks)</h2>
      <p>
        As per the{" "}
        <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
          University of Delhi Academic Council Resolution
        </a>{" "}
        on continuous internal evaluation, the 25-mark IA for each paper is distributed across the following components:
      </p>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Component</th>
              <th className="p-3 border-r border-rule">Maximum Marks</th>
              <th className="p-3 border-r border-rule">Evaluation Method</th>
              <th className="p-3">Typical Schedule</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Class Test 1 (Mid-Semester)</td>
              <td className="p-3 border-r border-rule font-semibold">5</td>
              <td className="p-3 border-r border-rule">Written test (30-45 min) covering syllabus taught up to mid-semester</td>
              <td className="p-3">Week 6-7 of the semester</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Class Test 2</td>
              <td className="p-3 border-r border-rule font-semibold">5</td>
              <td className="p-3 border-r border-rule">Written test covering the remaining portion of the syllabus</td>
              <td className="p-3">Week 12-13 of the semester</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Assignment / Project</td>
              <td className="p-3 border-r border-rule font-semibold">5</td>
              <td className="p-3 border-r border-rule">Written assignment (1,500-2,500 words), literature review, or data analysis project</td>
              <td className="p-3">Submitted by Week 10-12</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Presentation / Viva Voce</td>
              <td className="p-3 border-r border-rule font-semibold">5</td>
              <td className="p-3 border-r border-rule">Individual or group presentation (10-15 min) on an assigned topic, followed by Q&amp;A</td>
              <td className="p-3">Week 8-14 (varies)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Attendance &amp; Participation</td>
              <td className="p-3 border-r border-rule font-semibold">5</td>
              <td className="p-3 border-r border-rule">Based on attendance percentage and classroom participation quality</td>
              <td className="p-3">Entire semester (continuous)</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 2: Attendance Marks Calculation ─── */}
      <h2>2. Attendance-to-Marks Mapping</h2>
      <p>The 5 marks for attendance are awarded based on the following percentage bands:</p>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Attendance Percentage</th>
              <th className="p-3 border-r border-rule">IA Marks Awarded</th>
              <th className="p-3">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">90% and above</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">5 / 5</td>
              <td className="p-3">Full marks. No deduction.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">80% to 89%</td>
              <td className="p-3 border-r border-rule font-semibold">4 / 5</td>
              <td className="p-3">Standard for most regular students.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">70% to 79%</td>
              <td className="p-3 border-r border-rule font-semibold">3 / 5</td>
              <td className="p-3">Below this level, students should consider applying for medical/OD exemptions to boost percentage.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">60% to 69%</td>
              <td className="p-3 border-r border-rule font-semibold">2 / 5</td>
              <td className="p-3">At risk of falling below the 67% minimum attendance requirement for examination eligibility.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Below 60%</td>
              <td className="p-3 border-r border-rule font-semibold">1 / 5</td>
              <td className="p-3">Minimum 1 mark is typically awarded, but exam eligibility itself may be at risk.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 3: Grace Marks ─── */}
      <h2>3. DU Grace Marks Rules (Ordinance IX)</h2>
      <p>
        Grace marks are a supplementary marking provision that can be applied to help borderline students pass examinations. The rules governing grace marks are codified in DU Ordinance IX:
      </p>
      <ul>
        <li><strong>Maximum Grace Marks per Paper:</strong> 5 marks can be awarded in any single paper to help a student achieve the passing minimum (40% or Grade D).</li>
        <li><strong>Total Grace Marks per Semester:</strong> Maximum 10 marks across all papers combined in a single semester.</li>
        <li><strong>Automatic Application:</strong> Grace marks are applied automatically by the DU Exam Branch during result processing. Students do not need to apply separately for grace marks.</li>
        <li><strong>Transcript Notation:</strong> Papers where grace marks have been applied are typically not separately identified on the official transcript. The awarded grade reflects the post-grace-marks calculation.</li>
        <li><strong>Eligibility:</strong> Grace marks are available to all category students (UR, OBC, SC, ST, EWS, PwBD) equally.</li>
      </ul>
      <div className="my-6 border border-rule bg-card p-5 text-sm font-mono">
        <strong className="mono-label text-grade block text-base mb-2">Grace Marks Worked Example</strong>
        <p className="mt-2">
          Paper: Microeconomics I (Max: 100 = IA 25 + Exam 75)<br />
          Student scores: IA = 18/25, Exam = 19/75<br />
          Total = 18 + 19 = 37 out of 100 (needs 40 to pass)<br />
          Shortfall = 40 - 37 = 3 marks (within the 5-mark grace limit)<br />
          Result: Grace marks applied. Student gets 40/100 = Grade D (Pass)
        </p>
      </div>

      {/* ─── Section 4: IAMC Grievance Process ─── */}
      <h2>4. Internal Assessment Moderation Committee (IAMC) Grievance Process</h2>
      <p>
        If you believe your IA marks have been unfairly awarded — for example, if a teacher gave you 0/5 on attendance despite having 80%+ attendance, or awarded 2/5 on an assignment you submitted on time — you can file a formal grievance through the IAMC process:
      </p>
      <ol className="list-decimal list-inside space-y-4 text-sm my-6">
        <li>
          <strong>Step 1: Approach the Course Teacher Directly</strong><br />
          Before filing a formal grievance, speak to the teacher who awarded the IA marks. Many discrepancies are resolved informally (e.g., the teacher may have accidentally entered marks for the wrong student, or you may have been marked absent on a day you attended due to a biometric error).
        </li>
        <li>
          <strong>Step 2: Approach the Department IAMC</strong><br />
          Each department has an Internal Assessment Moderation Committee consisting of 3-5 faculty members. Write a formal application addressed to the IAMC Chairperson through the HoD, stating: your name, enrollment number, paper name, component disputed, marks expected vs marks received, and supporting evidence.
        </li>
        <li>
          <strong>Step 3: Compile Supporting Evidence</strong><br />
          Gather all evidence that supports your claim:
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Attendance records (screenshots from college ERP, signed attendance sheets)</li>
            <li>Class test answer sheets (if returned by the teacher)</li>
            <li>Assignment submission receipts (email timestamps, college submission register)</li>
            <li>Presentation slides or viva preparation notes</li>
            <li>WhatsApp/email correspondence with the teacher about IA evaluation</li>
          </ul>
        </li>
        <li>
          <strong>Step 4: IAMC Hearing</strong><br />
          The IAMC reviews the complaint, examines the evidence, and may call the student, the teacher, and the HoD for a hearing. This typically takes 7-14 working days from the date of complaint filing.
        </li>
        <li>
          <strong>Step 5: IAMC Decision</strong><br />
          The committee issues one of three outcomes:
          <ul className="list-disc list-inside ml-4 mt-1">
            <li><strong>Marks Upheld:</strong> The original IA marks stand. No change.</li>
            <li><strong>Marks Revised Upward:</strong> The IAMC directs the teacher to revise marks based on evidence.</li>
            <li><strong>Re-evaluation:</strong> In rare cases, the IAMC may assign a different faculty member to re-evaluate the assignment or class test.</li>
          </ul>
        </li>
        <li>
          <strong>Step 6: Appeal to the College Principal</strong><br />
          If the IAMC decision is unsatisfactory, you can appeal to the College Principal, who may escalate the matter to the Dean of Examinations or the University Anti-Ragging and Grievance Cell.
        </li>
      </ol>

      {/* ─── Section 5: Maximizing IA Marks ─── */}
      <h2>5. How to Maximize Your IA Score: Practical Strategies</h2>
      <div className="space-y-4 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Strategy 1: Front-Load Your Attendance</strong>
          <p className="text-sm">Attend every class in the first 6 weeks of the semester when most students are still settling in. This builds a buffer for any medical or personal absences later. Missing 5 classes out of 40 total puts you at 87.5% attendance — comfortably in the 4/5 marks zone.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Strategy 2: Never Submit Assignments Late</strong>
          <p className="text-sm">Many teachers apply a strict late submission penalty (1 mark deduction per day). An assignment scoring 4/5 on content becomes 2/5 if submitted 2 days late. Always submit at least one day before the deadline.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Strategy 3: Use Office Hours Before Class Tests</strong>
          <p className="text-sm">Visit the teacher during office hours 2-3 days before each class test to ask about the expected format, marking criteria, and weightage of different topics. Teachers reward students who show initiative.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Strategy 4: Maintain a Personal IA Record</strong>
          <p className="text-sm">Create a spreadsheet with columns for each IA component (Test 1, Test 2, Assignment, Presentation, Attendance) across all papers. Record your marks as they are disclosed. This becomes your primary evidence in case of any grievance.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Strategy 5: Request Marks Disclosure Before Exam Branch Upload</strong>
          <p className="text-sm">Under DU rules, IA marks must be displayed on the department notice board or communicated to students before they are uploaded to the Exam Branch portal. If your college does not do this voluntarily, file an RTI or request through the Student Union.</p>
        </div>
      </div>

      {/* ─── Section 6: IA Impact on SGPA/CGPA ─── */}
      <h2>6. Impact of IA Marks on Your SGPA &amp; CGPA</h2>
      <p>To understand why 25 IA marks matter so much, consider two students in the same Microeconomics I paper:</p>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Metric</th>
              <th className="p-3 border-r border-rule">Student A (High IA)</th>
              <th className="p-3">Student B (Low IA)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">IA Marks</td>
              <td className="p-3 border-r border-rule font-semibold">23 / 25</td>
              <td className="p-3">15 / 25</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">End-Semester Exam</td>
              <td className="p-3 border-r border-rule">52 / 75</td>
              <td className="p-3">52 / 75</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Total Marks</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">75 / 100 = Grade A (GP 8)</td>
              <td className="p-3">67 / 100 = Grade B+ (GP 7)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">SGPA Impact (4-credit paper)</td>
              <td className="p-3 border-r border-rule">Contributes 32 grade points (8 x 4) to total</td>
              <td className="p-3">Contributes 28 grade points (7 x 4) to total</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        The 8-mark difference in IA (23 vs 15) translates to a full grade level difference (A vs B+), which over 6 semesters can shift your final CGPA by 0.3 to 0.5 points. This difference can determine eligibility for DSE admission (7.5+ CGPA preferred), scholarship thresholds, and placement shortlists.
      </p>

      {/* ─── Section 7: Medical and OD Exemptions ─── */}
      <h2>7. Attendance Exemptions: Medical Leave &amp; On-Duty (OD)</h2>
      <ul>
        <li><strong>Medical Leave:</strong> Students can apply for medical exemptions by submitting a medical certificate from a registered medical practitioner to the college office within 7 days of returning to class. The college medical committee verifies the certificate and adjusts attendance records accordingly. Up to 15 working days of medical leave per semester can be exempted.</li>
        <li><strong>On-Duty (OD) for Extracurricular:</strong> Students participating in college festivals, university-level sports, NCC/NSS camps, or academic conferences can apply for OD exemptions. The Activity Coordinator signs the OD form, which is submitted to the Attendance Coordinator. OD days are counted as "present" for attendance calculation.</li>
        <li><strong>SC/ST/PwBD Relaxation:</strong> Students from SC, ST, and PwBD categories receive a 5-10% relaxation in the minimum attendance requirement (from 67% to 57-62%) as per UGC norms.</li>
      </ul>

      {/* ─── Section 8: Related Guides ─── */}
      <h2>8. Related College &amp; Academic Guides</h2>
      <ul>
        <li><a href="/blog/du-grading-attendance-explained" className="font-semibold underline text-grade">DU Grading and Attendance Rules Explained</a></li>
        <li><a href="/blog/du-exam-revaluation-rechecking-fees-2026" className="font-semibold underline text-grade">DU Revaluation vs Rechecking 2026 Guide</a></li>
        <li><a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="font-semibold underline text-grade">How to Calculate SGPA to CGPA in DU 2026</a></li>
        <li><a href="/blog/where-students-lose-marks-numericals" className="font-semibold underline text-grade">Where DU Students Lose Marks in Numericals</a></li>
        <li><a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">DU Elective Selection 2026: GE, SEC &amp; VAC Strategy</a></li>
      </ul>
    </>
  );
}


